// Area de envio de midia do totem: foto (JPG/PNG/WebP) ou video (MP4/WebM).
// Foto so e' reduzida (sem recorte) — o totem ajusta com object-fit; video
// vai como veio, limite de 40 MB (o nginx da loja aceita 50 MB).
//
// Dimensao recomendada (pedido do dono, 09/09/2026): totem costuma ser tela
// VERTICAL (totem, tablet em pe, TV em pe) → 1080 × 1920 px (9:16). Tela
// deitada: 1920 × 1080 px. A midia preenche a tela toda (object-cover), entao
// o que sobrar da proporcao e' cortado no centro — foto deitada num totem em
// pe perde as laterais. O card da lista avisa quando a orientacao nao bate.

import { Clapperboard, Loader2 } from "lucide-react";
import { useRef, useState } from "react";

const ACEITA = "image/*,video/mp4,video/webm,video/quicktime";
const MAX_VIDEO_MB = 40;

export default function TotemUploader({ onArquivo, enviando, desabilitado }) {
  const inputRef = useRef(null);
  const [arrastando, setArrastando] = useState(false);
  const [erro, setErro] = useState("");
  const bloqueado = enviando || desabilitado;

  const escolher = (file) => {
    if (!file || bloqueado) return;
    setErro("");
    const ehVideo = (file.type || "").startsWith("video/");
    if (ehVideo && file.size > MAX_VIDEO_MB * 1024 * 1024) {
      setErro(`Vídeo com ${(file.size / 1024 / 1024).toFixed(0)} MB. O limite é ${MAX_VIDEO_MB} MB — comprima antes de enviar.`);
      return;
    }
    if (!ehVideo && !(file.type || "").startsWith("image/")) {
      setErro("Envie uma foto (JPG, PNG, WebP) ou um vídeo (MP4, WebM).");
      return;
    }
    onArquivo(file);
  };

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); if (!bloqueado) setArrastando(true); }}
      onDragLeave={() => setArrastando(false)}
      onDrop={(e) => { e.preventDefault(); setArrastando(false); escolher(e.dataTransfer?.files?.[0]); }}
      className={`rounded-2xl border-2 border-dashed transition-colors ${
        arrastando ? "border-primary bg-primary/5" : "border-border"
      } ${bloqueado ? "opacity-60" : ""}`}
    >
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={bloqueado}
        data-e2e="totem-uploader"
        className="w-full flex flex-col items-center text-center px-6 py-10 disabled:cursor-not-allowed"
      >
        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-3">
          {enviando ? (
            <Loader2 className="w-6 h-6 text-primary animate-spin" />
          ) : (
            <Clapperboard className="w-6 h-6 text-primary" strokeWidth={1.5} />
          )}
        </div>
        <span className="text-sm font-semibold">
          {enviando ? "Enviando…" : "Adicionar foto ou vídeo"}
        </span>
        <span className="text-xs text-muted-foreground mt-1 max-w-md">
          {desabilitado
            ? "Limite de mídias atingido. Remova uma antes de adicionar outra."
            : `Clique ou arraste aqui. Foto JPG/PNG/WebP ou vídeo MP4/WebM até ${MAX_VIDEO_MB} MB.`}
        </span>
        {erro && <span className="text-xs text-destructive mt-2">{erro}</span>}
      </button>

      {!desabilitado && (
        <div className="px-6 pb-5 -mt-3 max-w-md mx-auto text-center space-y-1" data-e2e="totem-dimensoes">
          <p className="text-xs font-semibold">
            Tamanho ideal: <span className="text-primary">1080 × 1920 px</span> (9:16, em pé)
          </p>
          <p className="text-[11px] text-muted-foreground/90">
            Totem, tablet e TV em pé usam tela vertical. Tela deitada: 1920 × 1080 px (16:9).
            A mídia preenche a tela inteira — o que sobrar da proporção é cortado no centro,
            então foto deitada num totem em pé perde as laterais.
          </p>
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={ACEITA}
        className="hidden"
        onChange={(e) => { escolher(e.target.files?.[0]); e.target.value = ""; }}
      />
    </div>
  );
}
